import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert
} from '@mui/material';
import { API_BASE_URL } from '../../../config';
import { formatearFechaBD as formatFechaAR } from '../../../utils/reciboUtils';

const ESTADOS_CANCELABLES = ['pendiente_referente', 'pendiente_rh'];

function MisSolicitudes({ solicitudes, onCancelada }) {
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [solicitudACancelar, setSolicitudACancelar] = useState(null);
  const [cancelando, setCancelando] = useState(false);
  const [errorCancelar, setErrorCancelar] = useState('');

  const getEstadoColor = (estado) => {
    const estadoMap = {
      'pendiente_referente': 'warning',
      'pendiente_rh': 'info',
      'aprobado': 'success',
      'rechazado_referente': 'error',
      'rechazado_rh': 'error',
      'cancelado': 'default'
    };
    return estadoMap[estado] || 'default';
  };

  const getEstadoLabel = (estado) => {
    const labelMap = {
      'pendiente_referente': '⏳ Pendiente Referente',
      'pendiente_rh': '⏳ Pendiente RH',
      'aprobado': '✓ Aprobado',
      'rechazado_referente': '✗ Rechazado (Referente)',
      'rechazado_rh': '✗ Rechazado (RH)',
      'cancelado': '⊘ Cancelado'
    };
    return labelMap[estado] || estado;
  };

  const handleViewDetails = (solicitud) => {
    setSelectedSolicitud(solicitud);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedSolicitud(null);
  };

  const handleAbrirCancelar = (solicitud) => {
    setSolicitudACancelar(solicitud);
    setErrorCancelar('');
  };

  const handleConfirmarCancelar = async () => {
    if (!solicitudACancelar) return;
    setCancelando(true);
    setErrorCancelar('');

    try {
      const res = await fetch(`${API_BASE_URL}/api/vacaciones/cancelar/${solicitudACancelar.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Error al cancelar la solicitud');
      }

      setSolicitudACancelar(null);
      onCancelada && onCancelada();
    } catch (err) {
      setErrorCancelar(err.message);
    } finally {
      setCancelando(false);
    }
  };

  if (solicitudes.length === 0) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="textSecondary">
            No tienes solicitudes de vacaciones aún.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ background: '#f5f5f5' }}>
            <TableRow>
              <TableCell><strong>Período</strong></TableCell>
              <TableCell align="center"><strong>Días</strong></TableCell>
              <TableCell><strong>Estado</strong></TableCell>
              <TableCell align="center"><strong>Acciones</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {solicitudes.map((solicitud) => (
              <TableRow key={solicitud.id} hover>
                <TableCell>
                  {formatFechaAR(solicitud.fecha_inicio)} - {' '}
                  {formatFechaAR(solicitud.fecha_fin)}
                </TableCell>
                <TableCell align="center">
                  <Chip label={`${solicitud.dias_solicitados} días`} size="small" />
                </TableCell>
                <TableCell>
                  <Chip
                    label={getEstadoLabel(solicitud.estado)}
                    color={getEstadoColor(solicitud.estado)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleViewDetails(solicitud)}
                    >
                      Ver detalles
                    </Button>
                    {ESTADOS_CANCELABLES.includes(solicitud.estado) && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => handleAbrirCancelar(solicitud)}
                      >
                        Cancelar
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* DIALOG CON DETALLES */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>📋 Detalles de Solicitud</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedSolicitud && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="textSecondary">Período</Typography>
                <Typography variant="body1">
                  {formatFechaAR(selectedSolicitud.fecha_inicio)} a{' '}
                  {formatFechaAR(selectedSolicitud.fecha_fin)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="textSecondary">Días solicitados</Typography>
                <Typography variant="body1">{selectedSolicitud.dias_solicitados}</Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="textSecondary">Estado</Typography>
                <Chip
                  label={getEstadoLabel(selectedSolicitud.estado)}
                  color={getEstadoColor(selectedSolicitud.estado)}
                />
              </Box>

              {selectedSolicitud.comentarios_empleado && (
                <Box>
                  <Typography variant="body2" color="textSecondary">Tus comentarios</Typography>
                  <Typography variant="body2">{selectedSolicitud.comentarios_empleado}</Typography>
                </Box>
              )}

              {selectedSolicitud.referente_nombre && (
                <Box sx={{ p: 1.5, background: '#f9f9f9', borderRadius: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    ✓ Aprobado por: {selectedSolicitud.referente_nombre}
                  </Typography>
                  {selectedSolicitud.referente_comentario && (
                    <Typography variant="body2">
                      Comentario: {selectedSolicitud.referente_comentario}
                    </Typography>
                  )}
                  <Typography variant="caption" color="textSecondary">
                    Fecha: {new Date(selectedSolicitud.fecha_referente).toLocaleString('es-AR')}
                  </Typography>
                </Box>
              )}

              {selectedSolicitud.rh_nombre && (
                <Box sx={{ p: 1.5, background: '#f9f9f9', borderRadius: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    ✓ Aprobado por RH: {selectedSolicitud.rh_nombre}
                  </Typography>
                  {selectedSolicitud.rh_comentario && (
                    <Typography variant="body2">
                      Comentario: {selectedSolicitud.rh_comentario}
                    </Typography>
                  )}
                  <Typography variant="caption" color="textSecondary">
                    Fecha: {new Date(selectedSolicitud.fecha_rh).toLocaleString('es-AR')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG CONFIRMAR CANCELACIÓN */}
      <Dialog open={!!solicitudACancelar} onClose={() => !cancelando && setSolicitudACancelar(null)} maxWidth="xs" fullWidth>
        <DialogTitle>¿Cancelar esta solicitud?</DialogTitle>
        <DialogContent>
          {solicitudACancelar && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Se va a cancelar tu solicitud de <strong>{solicitudACancelar.dias_solicitados} días</strong> del{' '}
              {formatFechaAR(solicitudACancelar.fecha_inicio)} al{' '}
              {formatFechaAR(solicitudACancelar.fecha_fin)}. Esta acción no se puede deshacer;
              si querés esas fechas más adelante, vas a tener que cargar una solicitud nueva.
            </Typography>
          )}
          {errorCancelar && <Alert severity="error">{errorCancelar}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSolicitudACancelar(null)} disabled={cancelando}>
            Volver
          </Button>
          <Button
            onClick={handleConfirmarCancelar}
            variant="contained"
            color="error"
            disabled={cancelando}
          >
            {cancelando ? 'Cancelando...' : 'Sí, cancelar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default MisSolicitudes;
